import { colors } from '@apollo/brand';
import { gql, useQuery } from '@apollo/client';
import {
  Badge,
  CardDescription,
  CardHeader,
  CardTitle,
  Text,
  customColors,
} from '@apollo/orbit';
import moment from 'moment';
import React, { useMemo } from 'react';

import { useStatsVariables } from 'src/app/account/championDashboardPage/useStatsVariables';
import { EmptyLayout } from 'src/app/account/championDashboardPage/widgets/EmptyLayout';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { LineChart } from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { format } from 'src/lib/format';
import {
  SchemaPublishesWidgetQuery,
  SchemaPublishesWidgetQueryVariables,
  SchemaPublishesWidgetQuery_account_registryStatsWindow_schemaPublishStats,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from './ChampionDashboardCard';

export const SchemaPublishesWidget = ({ accountId }: { accountId: string }) => {
  const { timeFrom, timeTo, resolution } = useStatsVariables(60);

  const { data, loading } = useQuery<
    SchemaPublishesWidgetQuery,
    SchemaPublishesWidgetQueryVariables
  >(
    gql`
      query SchemaPublishesWidgetQuery(
        $accountId: ID!
        $timeFrom: Timestamp!
        $timeTo: Timestamp!
        $resolution: Resolution
      ) {
        account(id: $accountId) {
          id
          registryStatsWindow(
            from: $timeFrom
            to: $timeTo
            resolution: $resolution
          ) {
            schemaPublishStats {
              id
              timestamp
              metrics {
                totalPublishes
              }
            }
          }
        }
      }
    `,
    { variables: { accountId, timeFrom, timeTo, resolution } },
  );

  const publishesData = data?.account?.registryStatsWindow?.schemaPublishStats;

  const totalPublishes = useMemo(
    () =>
      publishesData?.reduce(
        (sum, col) => sum + col.metrics.totalPublishes,
        0,
      ) ?? 0,
    [publishesData],
  );

  const chartData = useMemo(
    () => [
      {
        key: 'data',
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        unit: ' publishes',
        data: publishesData ?? [],
        getValue: (
          stats: SchemaPublishesWidgetQuery_account_registryStatsWindow_schemaPublishStats,
        ) => ({
          timestamp: stats.timestamp,
          value: stats.metrics.totalPublishes,
        }),
      },
    ],
    [publishesData],
  );

  return (
    <ChampionDashboardCard className="col-span-full xl:col-span-3">
      <CardHeader>
        <CardTitle>
          Schema publishes <Badge variant="info">60 days</Badge>
        </CardTitle>
        {totalPublishes > 0 && (
          <CardDescription>
            <Text as="span" variant="bodySb" className="text-secondary">
              {format.number(totalPublishes, '0[.]0a')}
            </Text>{' '}
            total schema publishes
          </CardDescription>
        )}
      </CardHeader>
      <ChampionDashboardCardBody>
        {loading ? (
          <Loading />
        ) : totalPublishes > 0 ? (
          <CursorProvider>
            <LineChart
              stacked
              stacks={chartData}
              className="h-full pt-4"
              // pass in a 6 day tick interval to make sure it lines up with the other champion dashboard charts
              tickIntervalMinutes={moment.duration(6, 'days').asMinutes()}
              resolution={resolution}
            />
          </CursorProvider>
        ) : (
          <EmptyLayout
            title="Publish your schema"
            body="Publish your schema to start exploring Studio’s powerful features; see your schema’s types and fields, manage your graph, and track changes."
            learnMoreHref={DocsRouter.path('GettingStarted', {
              hash: '3-register-your-schema',
            })}
          />
        )}
      </ChampionDashboardCardBody>
    </ChampionDashboardCard>
  );
};
